import dayjs from "dayjs";
import {
  ApiOutlined,
  CalendarOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  HomeOutlined,
  IdcardOutlined,
  InfoCircleOutlined,
  KeyOutlined,
  MedicineBoxOutlined,
  SettingOutlined,
  TeamOutlined
} from "@ant-design/icons";

export const FILTERS_STORAGE_KEY = "user-monitor-filters";

export const pick = (obj, ...keys) => {
  for (const k of keys) {
    if (obj?.[k] != null) return obj[k];
  }
  return undefined;
};

export const toNumber = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export const getLawFlag = (law, index) => {
  if (!law || law.length <= index) return 1;
  const n = Number(law[index]);
  return Number.isFinite(n) ? n : 1;
};

export const formatDate = (value) => {
  if (!value) return "";
  const d = dayjs(value);
  return d.isValid() ? d.format("DD.MM.YYYY") : "";
};

export const formatTime = (value) => {
  if (!value) return "";
  const d = dayjs(value);
  return d.isValid() ? d.format("HH:mm:ss") : "";
};

/** Delphi: период по умолчанию — сегодня. */
export const defaultPeriod = () => {
  const today = dayjs().format("YYYY-MM-DD");
  return {
    dateBeg: today,
    dateEnd: today,
    vid: null,
    userName: null,
    q: "",
    sortBy: "fdatetime",
    sortDir: "asc"
  };
};

export const loadStoredFilters = () => {
  const defaults = defaultPeriod();
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw);
    return {
      dateBeg: parsed.dateBeg || defaults.dateBeg,
      dateEnd: parsed.dateEnd || defaults.dateEnd,
      vid: toNumber(parsed.vid),
      userName: parsed.userName || null,
      q: "",
      sortBy: parsed.sortBy || defaults.sortBy,
      sortDir: parsed.sortDir || defaults.sortDir
    };
  } catch {
    return defaults;
  }
};

export const saveFilters = (filters) => {
  try {
    localStorage.setItem(
      FILTERS_STORAGE_KEY,
      JSON.stringify({
        dateBeg: filters.dateBeg,
        dateEnd: filters.dateEnd,
        vid: filters.vid,
        userName: filters.userName,
        sortBy: filters.sortBy,
        sortDir: filters.sortDir
      })
    );
  } catch {
    /* ignore */
  }
};

export const normalizeListRow = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const idReg = toNumber(pick(raw, "idReg", "IdReg"));
  const vid = toNumber(pick(raw, "vid", "Vid")) ?? 0;
  const idCat =
    toNumber(pick(raw, "idCat", "IdCat")) ?? Math.floor(Math.abs(vid) / 100);
  return {
    idReg,
    fdate: pick(raw, "fdate", "Fdate") || null,
    fdatetime: pick(raw, "fdatetime", "Fdatetime") || null,
    vid,
    idCat,
    userName: pick(raw, "userName", "UserName") || "",
    objZagName: pick(raw, "objZagName", "ObjZagName") || "",
    objCntName: pick(raw, "objCntName", "ObjCntName") || "",
    komment: pick(raw, "komment", "Komment") || "",
    idObjZag: toNumber(pick(raw, "idObjZag", "IdObjZag")) ?? 0,
    idObjCnt: toNumber(pick(raw, "idObjCnt", "IdObjCnt")) ?? 0
  };
};

export const normalizeLookups = (data) => {
  const mapItems = (arr) => {
    const map = {};
    (Array.isArray(arr) ? arr : []).forEach((item) => {
      const id = toNumber(pick(item, "id", "Id"));
      const name = pick(item, "name", "Name");
      if (id != null && name) map[id] = String(name);
    });
    return map;
  };

  const vidList = (Array.isArray(pick(data, "vids", "Vids")) ? pick(data, "vids", "Vids") : [])
    .map((item) => {
      const id = toNumber(pick(item, "id", "Id"));
      const name = pick(item, "name", "Name");
      if (id == null) return null;
      return { value: id, label: name ? String(name) : String(id) };
    })
    .filter(Boolean);

  const users = (Array.isArray(pick(data, "users", "Users")) ? pick(data, "users", "Users") : [])
    .map((u) => {
      if (u == null) return "";
      if (typeof u === "string" || typeof u === "number") return String(u).trim();
      return String(pick(u, "name", "Name", "userName", "UserName") || "").trim();
    })
    .filter(Boolean)
    .map((name) => ({ value: name, label: name }));

  return {
    vids: mapItems(pick(data, "vids", "Vids")),
    cats: mapItems(pick(data, "cats", "Cats")),
    vidOptions: vidList,
    userOptions: users
  };
};

export const labelFromMap = (map, id, fallback) => {
  if (id == null) return fallback || "";
  return map?.[id] || fallback || String(id);
};

/** Иконки категорий журнала (ID_CAT = VID/100). Цвета — в CSS по data-cat. */
export const CAT_META = {
  11: { icon: CalendarOutlined },
  21: { icon: FileTextOutlined },
  22: { icon: MedicineBoxOutlined },
  23: { icon: HomeOutlined },
  24: { icon: TeamOutlined },
  40: { icon: SettingOutlined },
  50: { icon: IdcardOutlined },
  61: { icon: FileDoneOutlined },
  62: { icon: KeyOutlined },
  70: { icon: ApiOutlined }
};

export const DEFAULT_CAT_META = { icon: InfoCircleOutlined };

/** Убирает префикс категории из названия события: "Путевки. Заселение" → "Заселение". */
export const stripCatPrefix = (label) => {
  if (!label) return "";
  const s = String(label);
  const i = s.indexOf(". ");
  if (i < 0) return s;
  const rest = s.slice(i + 2).trim();
  return rest || s;
};

/** Тон карточки по тексту события. Порядок проверок важен: danger первым. */
export const actionTone = (vid, vidLabel) => {
  const t = String(vidLabel || "").toLowerCase();
  if (/(ошибка|провал|удаление|отмена|изъятие|снятие)/.test(t)) return "danger";
  if (/(добавление|создание|новое|новая|успех|подтвержден|заселение|оплата|вход)/.test(t))
    return "success";
  if (/(изменение|редактирование|пересчет|смена|блокировка|разблокировка|очистка)/.test(t))
    return "warning";
  return "neutral";
};
